/** Default sort used by the training list screen (client-side only). */
export const TRAINING_LIST_DEFAULTS = {
  sortBy: 'program',
  sortDir: 'asc',
};

/**
 * Stable cache key for a training list query.
 * Keyed by userId + courseId so each course filter (including "all") caches
 * separately (sort is client-side only).
 */
export function buildTrainingListCacheKey(userId, { courseId = '' } = {}) {
  const cid = (courseId || '').trim();
  return `trainings:${userId}:${cid || 'all'}`;
}

/**
 * Builds query string for training list API calls.
 * courseId is appended only when truthy ("" / undefined => all courses).
 */
export function buildTrainingListQuery({ courseId = '' } = {}) {
  const cid = (courseId || '').trim();
  if (!cid) return '';
  const params = new URLSearchParams();
  params.set('courseId', cid);
  return params.toString();
}

/** Sort keys exposed by the training table column headers. */
export const TRAINING_SORT_FIELDS = {
  program: 'program',
  audience: 'audience',
  completion: 'completion',
  due: 'due',
  type: 'type',
  status: 'status',
};

/**
 * Computes the next client-side sort when a column header is clicked.
 * Same column toggles asc/desc; a new column always starts asc.
 */
export function getNextTrainingSort(columnKey, currentSortBy, currentSortDir) {
  const sortBy = columnKey || TRAINING_LIST_DEFAULTS.sortBy;
  if (currentSortBy === sortBy) {
    return {
      sortBy,
      sortDir: currentSortDir === 'asc' ? 'desc' : 'asc',
    };
  }
  return { sortBy, sortDir: 'asc' };
}

/** Parses a millisecond-timestamp string into a number (0 when missing/invalid). */
function toTimestamp(value) {
  if (!value) return 0;
  const ts = parseInt(value, 10);
  return Number.isNaN(ts) ? 0 : ts;
}

/**
 * Resolves a row field value for client-side column sorting.
 * Numeric/date columns return numbers; everything else returns strings so the
 * caller can pick a numeric vs. locale string compare (mirrors enrollment sort).
 */
function trainingSortValue(row, sortBy) {
  switch (sortBy) {
    case TRAINING_SORT_FIELDS.audience:
      return row.courseName ?? '';
    case TRAINING_SORT_FIELDS.completion:
      // No completion fraction in the current API; coerce to a number so the
      // numeric compare path is used and the column stays sortable.
      return Number(row.completion ?? row.completionPercent ?? 0);
    case TRAINING_SORT_FIELDS.due:
      return toTimestamp(row.completionDate);
    case TRAINING_SORT_FIELDS.type:
      return row._type ?? (row.price ? 'mandatory' : 'optional');
    case TRAINING_SORT_FIELDS.status:
      return row._status ?? '';
    case TRAINING_SORT_FIELDS.program:
    default:
      return row.trainingName ?? '';
  }
}

/**
 * Sorts the current set of training rows in memory — no API round-trip.
 */
export function sortTrainingsLocally(rows, sortBy, sortDir) {
  const list = Array.isArray(rows) ? [...rows] : [];
  const dir = sortDir === 'desc' ? -1 : 1;
  const field = sortBy || TRAINING_LIST_DEFAULTS.sortBy;

  list.sort((a, b) => {
    const av = trainingSortValue(a, field);
    const bv = trainingSortValue(b, field);
    if (typeof av === 'number' && typeof bv === 'number') {
      return (av - bv) * dir;
    }
    return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * dir;
  });

  return list;
}
